import { scrollToElement, isDescendant, getTimes, Key, pad } from "./utils.js";

const NOOP = () => { };

let $toggles = Array.from(
  document.querySelectorAll('[is="lba-listbox"], [is="lba-menu"]')
);

$toggles.forEach(createListBox);

export async function createListBox(
  $toggle,
  { getOptions, onSelect = NOOP, onClose = NOOP, reinit = false, defaultLabel = '{label}' } = {}
) {
  let enabled = true;
  let $selected = null;
  let selectedIndex = -1;
  let role = $toggle.hasAttribute("is")
    ? $toggle.getAttribute("is").replace(/^lba-/, "")
    : "listbox";

  let id = $toggle.getAttribute("data-list");
  let $list = document.getElementById(id);
  let $input =
    role === "listbox"
      ? document.getElementById($toggle.getAttribute("data-input"))
      : null;

  let storedLabel = false;
  if (!storedLabel) storedLabel = $toggle.textContent;
  $toggle.textContent = storedLabel;

  if (getOptions) {
    let $spinner = $list.nextElementSibling;
    let spinnerTimeout = null;
    if ($spinner) {
      spinnerTimeout = window.setTimeout(() => {
        $spinner.hidden = false;
      }, 200);
    }

    let options = await Promise.resolve(getOptions());

    if ($spinner) {
      if (spinnerTimeout !== null) {
        window.clearTimeout(spinnerTimeout);
        spinnerTimeout = null;
      }
      $spinner.hidden = true;
    }

    let $optionTemplate = $list.querySelector("template").innerHTML;
    if ($optionTemplate) {
      $list.innerHTML =
        `<template>${$optionTemplate}</template>` +
        options.reduce((html, option) => {
          return (
            html +
            $optionTemplate.replace(/\{([^}]+)\}/g, (_, p1) => {
              return option[p1];
            })
          );
        }, "");
    } else {
      $list.innerHTML = options;
    }
  }

  $toggle.setAttribute("aria-haspopup", role);
  $list.setAttribute("tabindex", "-1");
  $list.setAttribute("role", role);

  if ($list.getAttribute("data-type") === "time") {
    let template = $list.querySelector("template").innerHTML;
    let html = "";

    let min = $list.hasAttribute('data-min') ? parseInt($list.getAttribute('data-min'), 10) : 0
    let max = $list.hasAttribute('data-max') ? parseInt($list.getAttribute('data-max'), 10) : 24

    let times = getTimes(min, max, 15);
    // match AeroParker times
    if (min === 0) {
      times[0] = "00:01";
    }
    if (max === 24) {
      times.push("23:59");
    } else {
      times.push(`${pad('00', max.toString())}:00`)
    }

    times.forEach(time => {
      html += template.replace("{label}", time);
    });
    $list.innerHTML = `<template>${template}</template>` + html;
  }

  let $options = Array.from($list.querySelectorAll("li"));

  $options.forEach(($option, i) => {
    $option.setAttribute("id", `${id}-${i}`);
    $option.setAttribute("role", role === "listbox" ? "option" : "menuitem");

    $option.addEventListener("click", selectAndClose);

    let val = $option.getAttribute("data-value");
    val = val === null ? $option.textContent : val;
    if ($input && val === $input.value) {
      select({ target: $option });
    }
  });

  if (!reinit) {
    $list.addEventListener("keydown", e => {
      if (!enabled) return;
      if (e.which === Key.ENTER) {
        e.preventDefault();
        close();
        $toggle.focus();
      } else if (e.which === Key.ESCAPE) {
        e.preventDefault();
        close();
        $toggle.focus();
      } else if (e.which === Key.HOME) {
        e.preventDefault();
        select({ target: $options[0] });
      } else if (e.which === Key.END) {
        e.preventDefault();
        select({ target: $options[$options.length - 1] });
      } else if (e.which === Key.UP) {
        e.preventDefault();
        let nextIndex = selectedIndex - 1;
        if (nextIndex >= 0) {
          select({ target: $options[nextIndex], index: nextIndex });
        }
      } else if (e.which === Key.DOWN) {
        e.preventDefault();
        let nextIndex = selectedIndex + 1;
        if (nextIndex < $options.length) {
          select({ target: $options[nextIndex], index: nextIndex });
        }
      }
    });

    $toggle.addEventListener("click", onclick);
  } else {
    $toggle.textContent = defaultLabel
  }

  function onclick() {
    if (!enabled) return;
    $list.hidden = !$list.hidden;

    if ($list.hidden) {
      $toggle.removeAttribute("aria-expanded");

      window.removeEventListener("focus", closeOnBlur, true);
      window.removeEventListener("click", closeOnBlur);
    } else {
      $toggle.setAttribute("aria-expanded", "true");

      if ($selected && $list.scrollHeight > $list.clientHeight) {
        let elementMiddle = $selected.offsetTop + $selected.offsetHeight / 2;
        $list.scrollTop = elementMiddle - $list.clientHeight / 2;
      }

      $list.focus();

      window.addEventListener("focus", closeOnBlur, true);
      window.addEventListener("click", closeOnBlur);
    }
  }

  function close() {
    $list.hidden = true;
    $toggle.removeAttribute("aria-expanded");
    window.removeEventListener("focus", closeOnBlur, true);
    window.removeEventListener("click", closeOnBlur);
    onClose(api);
  }

  function closeOnBlur(e) {
    if (
      e.target !== $list &&
      e.target !== $toggle &&
      !isDescendant($list, e.target) &&
      !isDescendant($toggle, e.target)
    ) {
      close();
    }
  }

  function select(e) {
    if (!enabled) return;
    let $option = e.target.closest("li");
    if (role === "menu") {
      if ($toggle.getAttribute("data-action") === "scroll-to") {
        let id = $option.getAttribute("data-value");
        scrollToElement(document.getElementById(id));
      }
      return;
    }

    let label = $option.getAttribute("data-label");
    $toggle.textContent = label === null ? $option.textContent : label;
    $list.setAttribute("aria-activedescendant", $option.id);

    const $departingAirlines = [
      ...document.querySelectorAll(".js-parking-widget-v2-departing-airline")
    ];
    if (
      $option.getAttribute("data-departing-airline") &&
      $departingAirlines.length
    ) {
      $departingAirlines.forEach($departingAirline => {
        $departingAirline.value = $option.getAttribute(
          "data-departing-airline"
        );
      });
    }
    const $departingTimes = [
      ...document.querySelectorAll(".js-parking-widget-v2-departing-time")
    ];
    if ($option.getAttribute("data-departing-time") && $departingTimes.length) {
      $departingTimes.forEach($departingTime => {
        $departingTime.value = $option.getAttribute("data-departing-time");
      });
    }
    const $returningAirlines = [
      ...document.querySelectorAll(".js-parking-widget-v2-returning-airline")
    ];
    if (
      $option.getAttribute("data-returning-airline") &&
      $returningAirlines.length
    ) {
      $returningAirlines.forEach($returningAirline => {
        $returningAirline.value = $option.getAttribute(
          "data-returning-airline"
        );
      });
    }
    const $returningTimes = [
      ...document.querySelectorAll(".js-parking-widget-v2-returning-time")
    ];
    if ($option.getAttribute("data-returning-time") && $returningTimes.length) {
      $returningTimes.forEach($returningTime => {
        $returningTime.value = $option.getAttribute("data-returning-time");
      });
    }

    if ($selected) {
      $selected.removeAttribute("aria-selected");
    }

    $option.setAttribute("aria-selected", "true");
    let val = $option.getAttribute("data-value");
    if ($input) {
      $input.value = val === null ? $option.textContent : val;
    }

    $selected = $option;
    if (e.index) {
      selectedIndex = e.index;
    } else {
      selectedIndex = $options.indexOf($selected);
    }

    if ($list.scrollHeight > $list.clientHeight) {
      let scrollBottom = $list.clientHeight + $list.scrollTop;
      let elementBottom = $selected.offsetTop + $selected.offsetHeight;
      if (elementBottom > scrollBottom) {
        $list.scrollTop = elementBottom - $list.clientHeight;
      } else if ($selected.offsetTop < $list.scrollTop) {
        $list.scrollTop = $selected.offsetTop;
      }
    }

    onSelect(api);
  }

  function selectAndClose(e) {
    if (!enabled) return;
    select(e);
    close();
  }

  let api = {
    $list,
    getValue() {
      return $input ? $input.value : null;
    },
    setValue(val) {
      let $option;
      $options.some($o => {
        if ($o.textContent === val) {
          $option = $o;
          return true;
        }
        return false;
      });
      if ($option) {
        select({ target: $option });
      }
    },
    enable() {
      enabled = true;
      $input.removeAttribute("readonly");
      $toggle.disabled = false;
    },
    disable() {
      enabled = false;
      $input.setAttribute("readonly", "");
      $toggle.disabled = true;
    }
  };

  return api;
}



// WEBPACK FOOTER //
// ./src/js/_listbox.js